import { normalizeProviderSurfaceKey } from "./providerSurfaceRegistry";
import {
  aggregateMetricBasis,
  aggregateAllCountRatePairs,
  aggregateCostPerQualifiedVideoView,
  aggregateQualifiedVideoViews,
} from "../platformAnalytics/metricBasis";
import { buildQualifiedVideoPresentation } from "../platformAnalytics/qualifiedVideoPresentation";

const toNumber = (value) => {
  if (value == null || value === "") return NaN;
  if (typeof value === "number") return value;
  const normalized = String(value).replace(/[$,%\s,]/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const pickFinite = (...values) => {
  for (const value of values) {
    const parsed = toNumber(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const resolveClientFacingSpend = (source = {}, fallbackSpend = 0) => {
  const pricing = source?.pricing && typeof source.pricing === "object" ? source.pricing : {};
  const pricingMetrics =
    source?.pricingMetrics && typeof source.pricingMetrics === "object" ? source.pricingMetrics : {};
  const commercial =
    source?.aggregateCommercialMetrics && typeof source.aggregateCommercialMetrics === "object"
      ? source.aggregateCommercialMetrics
      : source?.commercialMetrics && typeof source.commercialMetrics === "object"
      ? source.commercialMetrics
      : source?.clientBillingAttribution && typeof source.clientBillingAttribution === "object"
      ? source.clientBillingAttribution
      : {};

  const explicit = pickFinite(
    source.clientFacingSpend,
    source.client_facing_spend,
    source.billedSpend,
    source.billed_spend,
    source.clientBilledSpend,
    source.client_billed_spend,
    source.effectiveSpend,
    source.effective_spend,
    source.billedCost,
    source.billed_cost,
    pricing.billedSpend,
    pricing.billed_spend,
    pricing.billedCost,
    pricing.billed_cost,
    pricingMetrics.billedSpend,
    pricingMetrics.billed_spend,
    pricingMetrics.billedCost,
    pricingMetrics.billed_cost,
    commercial.clientFacingSpend,
    commercial.client_facing_spend,
    commercial.billedSpend,
    commercial.billed_spend,
    commercial.billedCost,
    commercial.billed_cost,
    commercial.effectiveSpend,
    commercial.effective_spend
  );

  return explicit != null ? explicit : fallbackSpend;
};

const resolveDisplaySpend = (source = {}, fallbackSpend = null) =>
  pickFinite(
    source.clientFacingSpend,
    source.client_facing_spend,
    source.clientSpend,
    source.client_spend,
    source.spendClientFacing,
    source.spend_client_facing,
    source.clientBilledSpend,
    source.client_billed_spend,
    source.billedSpend,
    source.billed_spend,
    source.effectiveSpend,
    source.effective_spend,
    source.reportSpend,
    source.report_spend,
    source.snapshotSpend,
    source.snapshot_spend,
    source.spend,
    source.spend_usd,
    source.rawSpend,
    source.raw_spend,
    source.platformSpend,
    source.platform_spend,
    source.mediaSpend,
    source.media_spend,
    source.deliverySpend,
    source.delivery_spend,
    source.providerSpend,
    source.provider_spend,
    source.cost,
    source.amountSpent,
    source.amount_spent,
    fallbackSpend
  );

const resolveClientFacingCpm = (source = {}, impressions = 0, clientFacingSpend = 0, fallbackCpm = null) => {
  const pricing = source?.pricing && typeof source.pricing === "object" ? source.pricing : {};
  const pricingMetrics =
    source?.pricingMetrics && typeof source.pricingMetrics === "object" ? source.pricingMetrics : {};
  const commercial =
    source?.aggregateCommercialMetrics && typeof source.aggregateCommercialMetrics === "object"
      ? source.aggregateCommercialMetrics
      : source?.commercialMetrics && typeof source.commercialMetrics === "object"
      ? source.commercialMetrics
      : source?.clientBillingAttribution && typeof source.clientBillingAttribution === "object"
      ? source.clientBillingAttribution
      : {};

  const explicit = pickFinite(
    source.clientFacingCpm,
    source.client_facing_cpm,
    source.billedCpm,
    source.billed_cpm,
    source.clientBilledCpm,
    source.client_billed_cpm,
    source.effectiveCpm,
    source.effective_cpm,
    pricing.billedCpm,
    pricing.billed_cpm,
    pricingMetrics.billedCpm,
    pricingMetrics.billed_cpm,
    commercial.clientFacingCpm,
    commercial.client_facing_cpm,
    commercial.billedCpm,
    commercial.billed_cpm,
    commercial.effectiveCpm,
    commercial.effective_cpm
  );

  if (explicit != null) return explicit;
  if (
    Number.isFinite(impressions) &&
    impressions > 0 &&
    Number.isFinite(clientFacingSpend) &&
    clientFacingSpend > 0
  ) {
    return (clientFacingSpend / impressions) * 1000;
  }
  return fallbackCpm;
};

const resolveClientFacingCpc = (source = {}, clicks = 0, clientFacingSpend = 0, fallbackCpc = null) => {
  const pricing = source?.pricing && typeof source.pricing === "object" ? source.pricing : {};
  const pricingMetrics =
    source?.pricingMetrics && typeof source.pricingMetrics === "object" ? source.pricingMetrics : {};
  const commercial =
    source?.aggregateCommercialMetrics && typeof source.aggregateCommercialMetrics === "object"
      ? source.aggregateCommercialMetrics
      : source?.commercialMetrics && typeof source.commercialMetrics === "object"
      ? source.commercialMetrics
      : source?.clientBillingAttribution && typeof source.clientBillingAttribution === "object"
      ? source.clientBillingAttribution
      : {};

  const explicit = pickFinite(
    source.clientFacingCpc,
    source.client_facing_cpc,
    source.billedCpc,
    source.billed_cpc,
    source.clientBilledCpc,
    source.client_billed_cpc,
    source.effectiveCpc,
    source.effective_cpc,
    pricing.billedCpc,
    pricing.billed_cpc,
    pricingMetrics.billedCpc,
    pricingMetrics.billed_cpc,
    commercial.clientFacingCpc,
    commercial.client_facing_cpc,
    commercial.billedCpc,
    commercial.billed_cpc,
    commercial.effectiveCpc,
    commercial.effective_cpc
  );

  if (explicit != null) return explicit;
  if (
    Number.isFinite(clicks) &&
    clicks > 0 &&
    Number.isFinite(clientFacingSpend) &&
    clientFacingSpend > 0
  ) {
    return clientFacingSpend / clicks;
  }
  return fallbackCpc;
};

const canonicalizeChannelKey = (value) => {
  const providerKey = normalizeProviderSurfaceKey(value);
  if (providerKey) return providerKey;
  const raw = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[()]/g, " ")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

  if (!raw) return "";
  if (raw.includes("facebook")) return "facebook";
  if (raw.includes("instagram")) return "instagram";
  if (
    raw === "audience_network" ||
    raw.includes("audience network") ||
    (raw.includes("audience") && raw.includes("network"))
  ) {
    return "audience_network";
  }
  if (raw.includes("messenger")) return "messenger";
  if (raw.includes("google analytics") || raw === "ga4" || raw === "ga") {
    return "google_analytics";
  }
  if (raw === "google_search" || raw.includes("google search") || raw.includes("paid search")) {
    return "google_search";
  }
  if (raw.includes("stackadapt") || raw.includes("programmatic")) return "programmatic";
  if (raw.includes("truth social")) return "truth_social";
  return raw.replace(/\s+/g, "_");
};

const mergeChannelAgg = (base = {}, incoming = {}) => {
  const out = { ...(base || {}) };
  const sumFields = [
    "spend",
    "spend_usd",
    "clientFacingSpend",
    "client_facing_spend",
    "billedSpend",
    "billed_spend",
    "clientBilledSpend",
    "client_billed_spend",
    "effectiveSpend",
    "effective_spend",
    "impressions",
    "clicks",
    "engagements",
    "video_views",
    "videoViews",
    "conversions",
    "video_started",
    "videoStarts",
    "video_start",
    "video_starts",
    "video_completed",
    "videoCompletes",
    "video_complete",
    "completed_views",
    "completionRate",
    "conversion_value",
    "conversionValue",
    "conversion_value_usd",
    "conversionValueUsd",
    "ticket_sales",
    "ticketSales",
    "ticket_sales_usd",
    "ticketSalesUsd",
    "users",
    "active_users",
    "activeUsers",
    "new_users",
    "newUsers",
    "sessions",
    "engaged_sessions",
    "engagedSessions",
    "event_count",
    "eventCount",
    "page_views",
    "pageViews",
    "screen_page_views",
    "screenPageViews",
    "key_events",
    "keyEvents",
    "avg_session_duration",
    "averageSessionDuration",
  ];
  const maxFields = [
    "total_booked_theaters",
    "totalBookedTheaters",
    "total_circuits",
    "totalCircuits",
    "total_cities",
    "totalCities",
    "total_states",
    "totalStates",
  ];

  for (const key of Object.keys(incoming || {})) {
    const value = incoming[key];
    if (maxFields.includes(key)) {
      const a = toNumber(out[key] ?? -Infinity);
      const b = toNumber(value ?? -Infinity);
      if (Number.isFinite(a) || Number.isFinite(b)) {
        out[key] = Math.max(Number.isFinite(a) ? a : -Infinity, Number.isFinite(b) ? b : -Infinity);
      }
      continue;
    }
    if (sumFields.includes(key)) {
      const a = toNumber(out[key] ?? 0);
      const b = toNumber(value ?? 0);
      const aOk = Number.isFinite(a);
      const bOk = Number.isFinite(b);
      if (aOk || bOk) out[key] = (aOk ? a : 0) + (bOk ? b : 0);
      continue;
    }
    out[key] = value;
  }

  return out;
};

const hasUsableMetrics = (metrics) => {
  if (!metrics) return false;
  const checks = [
    metrics.impressions,
    metrics.spend,
    metrics.spend_usd,
    metrics.reportSpend,
    metrics.report_spend,
    metrics.snapshotSpend,
    metrics.snapshot_spend,
    metrics.clientFacingSpend,
    metrics.client_facing_spend,
    metrics.billedSpend,
    metrics.billed_spend,
    metrics.reportImpressions,
    metrics.report_impressions,
    metrics.snapshotImpressions,
    metrics.snapshot_impressions,
    metrics.clicks,
    metrics.video_views,
    metrics.videoViews,
    metrics.conversions,
    metrics.engagements,
    metrics.users,
    metrics.active_users,
    metrics.activeUsers,
    metrics.sessions,
    metrics.page_views,
    metrics.pageViews,
    metrics.screen_page_views,
    metrics.screenPageViews,
    metrics.event_count,
    metrics.eventCount,
    metrics.key_events,
    metrics.keyEvents,
  ];
  return checks.some((value) => {
    const parsed = toNumber(value ?? 0);
    return Number.isFinite(parsed) && parsed > 0;
  });
};

const resolveFieldNumber = (source = {}, keys = []) => {
  for (const key of keys) {
    const value = pickFinite(source?.[key]);
    if (value != null) return value;
  }

  const pricing = source?.pricing && typeof source.pricing === "object" ? source.pricing : {};
  const pricingMetrics =
    source?.pricingMetrics && typeof source.pricingMetrics === "object" ? source.pricingMetrics : {};
  const commercial =
    source?.aggregateCommercialMetrics && typeof source.aggregateCommercialMetrics === "object"
      ? source.aggregateCommercialMetrics
      : source?.commercialMetrics && typeof source.commercialMetrics === "object"
      ? source.commercialMetrics
      : source?.clientBillingAttribution && typeof source.clientBillingAttribution === "object"
      ? source.clientBillingAttribution
      : {};

  for (const container of [pricing, pricingMetrics, commercial]) {
    for (const key of keys) {
      const value = pickFinite(container?.[key]);
      if (value != null) return value;
    }
  }

  return null;
};

const getPreferredSpend = (source = {}) =>
  resolveFieldNumber(source, [
    "reportSpend",
    "report_spend",
    "clientFacingSpend",
    "client_facing_spend",
    "billedSpend",
    "billed_spend",
    "snapshotSpend",
    "snapshot_spend",
    "spend",
    "spend_usd",
  ]);

const getPreferredImpressions = (source = {}) =>
  resolveFieldNumber(source, [
    "reportImpressions",
    "report_impressions",
    "snapshotImpressions",
    "snapshot_impressions",
    "clientFacingImpressions",
    "client_facing_impressions",
    "impressions",
  ]);

const getSourceLabel = (source = {}) =>
  String(
    source?.sourceLabel ||
      source?.source_label ||
      source?.data_source ||
      source?.provider ||
      source?.source ||
      ""
  )
    .trim()
    .toLowerCase();

const isCanonicalReportingSource = (source = {}) => {
  const dataSource = String(source?.data_source || source?.dataSource || "")
    .trim()
    .toLowerCase();
  const sourceLabel = getSourceLabel(source);
  const contract = String(source?.sourceContract?.contract || source?.contract || "")
    .trim()
    .toLowerCase();
  return (
    dataSource.includes("canonical") ||
    sourceLabel.includes("canonical performance summary") ||
    contract === "canonical_reporting_v1"
  );
};

const getSpendBasis = (source = {}) =>
  String(source?.spendBasis || source?.spend_basis || "")
    .trim()
    .toLowerCase();

const resolveChannelSpendBasis = (source = {}, fallback = "provider_raw") => {
  const explicit = getSpendBasis(source);
  if (explicit) return explicit;
  if (
    resolveFieldNumber(source, [
      "reportSpend",
      "report_spend",
      "reportImpressions",
      "report_impressions",
      "snapshotSpend",
      "snapshot_spend",
      "snapshotImpressions",
      "snapshot_impressions",
    ]) != null
  ) {
    return "report_snapshot";
  }
  if (
    resolveFieldNumber(source, [
      "clientFacingSpend",
      "client_facing_spend",
      "billedSpend",
      "billed_spend",
    ]) != null
  ) {
    return "client_billed";
  }
  return fallback;
};

const resolveClientFacingChannelMetrics = (source = {}) => {
  const spend = getPreferredSpend(source);
  const impressions = getPreferredImpressions(source);
  const clicks = resolveFieldNumber(source, ["reportClicks", "report_clicks", "clicks"]);
  const spendBasis = resolveChannelSpendBasis(source);
  const sourceLabel = getSourceLabel(source);
  const hasPricingSignals =
    resolveFieldNumber(source, [
      "clientFacingSpend",
      "client_facing_spend",
      "billedSpend",
      "billed_spend",
      "reportSpend",
      "report_spend",
      "snapshotSpend",
      "snapshot_spend",
    ]) != null;
  const hasReportLikeDelivery =
    resolveFieldNumber(source, [
      "reportImpressions",
      "report_impressions",
      "snapshotImpressions",
      "snapshot_impressions",
      "clientFacingImpressions",
      "client_facing_impressions",
    ]) != null;

  return {
    spend,
    impressions,
    clicks,
    spendBasis,
    sourceLabel,
    hasPricingSignals,
    hasReportLikeDelivery,
  };
};

const isMixedSourceChannelCandidate = (source = {}) => {
  const { spendBasis, sourceLabel, hasPricingSignals, hasReportLikeDelivery } =
    resolveClientFacingChannelMetrics(source);

  return Boolean(
    hasPricingSignals ||
      hasReportLikeDelivery ||
      spendBasis === "client_billed" ||
      spendBasis === "report_snapshot" ||
      sourceLabel.includes("report") ||
      sourceLabel.includes("snapshot")
  );
};

const getChannelSourceScore = (channelKey, source = {}) => {
  if (!source || typeof source !== "object") return Number.NEGATIVE_INFINITY;

  const {
    spendBasis,
    sourceLabel,
    spend: preferredSpend,
    impressions: preferredImpressions,
    hasPricingSignals,
    hasReportLikeDelivery,
  } = resolveClientFacingChannelMetrics(source);
  const spend = Number.isFinite(preferredSpend) ? preferredSpend : 0;
  const impressions = Number.isFinite(preferredImpressions) ? preferredImpressions : 0;

  let score = 0;

  if (Number.isFinite(spend) && spend > 0) score += 1000 + spend;
  if (Number.isFinite(impressions) && impressions > 0) score += 100 + impressions / 1000;

  if (spendBasis === "client_billed" || spendBasis === "report_snapshot") score += 10000;
  if (sourceLabel.includes("report") || sourceLabel.includes("snapshot")) score += 6000;
  // "cphere omni" is the client-safe name of the same live Omni Channel aggregate: it ranks exactly as the feed it renames.
  if (sourceLabel.includes("stackadapt") || sourceLabel.includes("live") || sourceLabel === "cphere omni") score -= 500;

  if (hasPricingSignals) score += 25000;
  if (hasReportLikeDelivery) score += 5000;
  if (channelKey && sourceLabel.includes(channelKey.replace(/_/g, " "))) score += 25;

  return score;
};

const decorateChosenSource = (source = {}, fallbackDataSource = "unknown", selectionReason = "fallback") => {
  const {
    spend: preferredSpend,
    impressions: preferredImpressions,
    spendBasis,
  } = resolveClientFacingChannelMetrics(source);
  const rawSpend = pickFinite(source?.spend ?? source?.spend_usd);
  const rawImpressions = pickFinite(source?.impressions);
  const normalizedSpend =
    preferredSpend != null && (!Number.isFinite(rawSpend) || preferredSpend >= rawSpend)
      ? preferredSpend
      : rawSpend;
  const normalizedImpressions =
    preferredImpressions != null &&
    (!Number.isFinite(rawImpressions) || preferredImpressions >= rawImpressions)
      ? preferredImpressions
      : rawImpressions;

  return {
    ...(source || {}),
    spend: normalizedSpend,
    impressions: normalizedImpressions,
    clientFacingSpend:
      source?.clientFacingSpend != null || source?.client_facing_spend != null
        ? source?.clientFacingSpend ?? source?.client_facing_spend
        : preferredSpend,
    data_source: source?.data_source || fallbackDataSource,
    sourceLabel:
      source?.sourceLabel ||
      source?.source_label ||
      source?.data_source ||
      source?.provider ||
      fallbackDataSource,
    spendBasis,
    selectionReason,
  };
};

const chooseBestChannelView = (channelKey, { live, previous, ingest }) => {
  const candidates = [
    { type: "live", value: live },
    { type: "ingest", value: ingest },
    { type: "previous", value: previous },
  ].filter((candidate) => hasUsableMetrics(candidate.value));

  const canonicalCandidate = candidates.find((candidate) =>
    isCanonicalReportingSource(candidate.value)
  );
  if (canonicalCandidate) {
    return decorateChosenSource(
      canonicalCandidate.value,
      canonicalCandidate.type,
      `canonical_reporting_preferred_for_${channelKey}`
    );
  }

  if (candidates.length > 0) {
    const ranked = candidates
      .map((candidate) => ({
        ...candidate,
        score: getChannelSourceScore(channelKey, candidate.value),
      }))
      .sort((a, b) => b.score - a.score);
    const winner = ranked[0];
    if (process.env.NODE_ENV !== "test" && ranked.some((candidate) => isMixedSourceChannelCandidate(candidate.value))) {
      console.debug("[reportingTruthResolver][candidate-trace]", {
        channel: channelKey,
        candidates: ranked.map((candidate, index) => ({
          index,
          type: candidate.type,
          score: candidate.score,
          sourceLabel: getSourceLabel(candidate.value),
          spendBasis: resolveChannelSpendBasis(candidate.value),
          spend: candidate.value?.spend ?? candidate.value?.spend_usd ?? null,
          reportSpend: candidate.value?.reportSpend ?? candidate.value?.report_spend ?? null,
          clientFacingSpend:
            candidate.value?.clientFacingSpend ?? candidate.value?.client_facing_spend ?? null,
          billedSpend: candidate.value?.billedSpend ?? candidate.value?.billed_spend ?? null,
          snapshotSpend:
            candidate.value?.snapshotSpend ?? candidate.value?.snapshot_spend ?? null,
          impressions: candidate.value?.impressions ?? null,
          reportImpressions:
            candidate.value?.reportImpressions ?? candidate.value?.report_impressions ?? null,
          snapshotImpressions:
            candidate.value?.snapshotImpressions ?? candidate.value?.snapshot_impressions ?? null,
        })),
        selectedType: winner.type,
      });
    }
    return decorateChosenSource(
      winner.value,
      winner.type,
      `${winner.type}_preferred_for_${channelKey}`
    );
  }

  if (ingest) return decorateChosenSource(ingest, "ingest", `ingest_fallback_for_${channelKey}`);
  return null;
};

const buildIngestByChannel = (aggregate) => {
  const by = {};
  const sourceContract =
    aggregate?.sourceContract && typeof aggregate.sourceContract === "object"
      ? aggregate.sourceContract
      : {};
  const aggregateDataSource =
    isCanonicalReportingSource({
      data_source: aggregate?.data_source || aggregate?.dataSource,
      sourceLabel: aggregate?.sourceLabel,
      sourceContract,
    })
      ? "canonical_reporting_summary"
      : null;
  const raw =
    aggregate?.byChannel && typeof aggregate.byChannel === "object" ? aggregate.byChannel : {};
  for (const [rawKey, value] of Object.entries(raw)) {
    const key = canonicalizeChannelKey(rawKey);
    if (!key) continue;
    const normalizedValue = aggregateDataSource
      ? {
          ...(value || {}),
          data_source: aggregateDataSource,
          sourceLabel:
            value?.sourceLabel || value?.source_label || "Canonical Performance Summary",
          sourceContract,
        }
      : value || {};
    by[key] = by[key] ? mergeChannelAgg(by[key], normalizedValue) : normalizedValue;
  }
  return by;
};

const computeResolvedTotals = (resolvedByChannel) => {
  let totals = {};
  let cinemaOnly = {};

  for (const [key, channel] of Object.entries(resolvedByChannel || {})) {
    if (!channel) continue;
    if (key === "cinema") {
      cinemaOnly = mergeChannelAgg(cinemaOnly, {
        ticket_sales:
          channel.ticket_sales ??
          channel.ticketSales ??
          channel.ticket_sales_usd ??
          channel.ticketSalesUsd,
        ticketSales: channel.ticketSales,
        ticket_sales_usd: channel.ticket_sales_usd,
        ticketSalesUsd: channel.ticketSalesUsd,
        total_booked_theaters: channel.total_booked_theaters ?? channel.totalBookedTheaters,
        totalBookedTheaters: channel.totalBookedTheaters,
        total_circuits: channel.total_circuits ?? channel.totalCircuits,
        totalCircuits: channel.totalCircuits,
        total_cities: channel.total_cities ?? channel.totalCities,
        totalCities: channel.totalCities,
        total_states: channel.total_states ?? channel.totalStates,
        totalStates: channel.totalStates,
        conversion_value:
          channel.conversion_value ??
          channel.conversionValue ??
          channel.conversion_value_usd ??
          channel.conversionValueUsd,
        conversionValue: channel.conversionValue,
        conversion_value_usd: channel.conversion_value_usd,
        conversionValueUsd: channel.conversionValueUsd,
      });
      continue;
    }

    totals = mergeChannelAgg(totals, channel);
  }

  return mergeChannelAgg(totals, cinemaOnly);
};

const computeRatePercent = (numerator, denominator) => {
  const num = toNumber(numerator ?? 0);
  const den = toNumber(denominator ?? 0);
  if (!Number.isFinite(num) || !Number.isFinite(den) || den <= 0) return null;
  const pct = (num / den) * 100;
  return Number.isFinite(pct) ? pct : null;
};

const computeReportingMetrics = (totals, resolvedByChannel = null, { preserveMissingMetrics = false } = {}) => {
  const safeTotals = totals && typeof totals === "object" ? totals : null;
  if (!safeTotals) return null;
  const unavailableMetric = preserveMissingMetrics || Object.keys(resolvedByChannel || {}).some(normalizeProviderSurfaceKey) ? null : 0;

  /*
   * When the resolution kept per-channel evidence, every blended rate folds
   * through the canonical eligibility engine — the same byte-pinned module
   * every other surface uses — so a channel that cannot report a metric
   * never sits in its denominator. A governed null is an answer and does not
   * fall through to whole-pool division; the whole-pool arithmetic below
   * survives only for totals-only inputs, where whole-scope and
   * single-scope coincide.
   */
  const governedObservations =
    resolvedByChannel && typeof resolvedByChannel === "object" && Object.keys(resolvedByChannel).length
      ? Object.entries(resolvedByChannel).map(([channel, entry]) => ({
          platformKey: channel,
          sourceKey: entry?.sourceLabel || entry?.source || channel,
          metrics: entry?.metrics && typeof entry.metrics === "object" ? entry.metrics : entry,
        }))
      : null;
  const governedBases = governedObservations
    ? Object.fromEntries(
        ["ctr", "engagementRate", "vvr", "cpc", "cpm", "cpa", "completionRate"].map((metricKey) => [
          metricKey,
          aggregateMetricBasis(metricKey, governedObservations),
        ])
      )
    : null;
  /*
   * The counts that share a card with a rate, and the campaign-level video
   * headline — folded from the same observations the rates were, so a page can
   * never state a completion count on one platform population and its rate on
   * another, nor sum a video total across platforms the registry has not ruled
   * compatible.
   */
  const governedPairs = governedObservations
    ? aggregateAllCountRatePairs(governedObservations)
    : null;
  const governedQualifiedVideo = (() => {
    if (!governedObservations) return null;
    const basis = aggregateQualifiedVideoViews(governedObservations);
    /* The presentation module writes the "combines X + Y" sentence from the
       registry's own native labels, so a surface never names platforms
       itself. Attached to the basis so the two always travel together. */
    const presentation = buildQualifiedVideoPresentation(basis);
    return presentation ? { ...basis, explainer: presentation.explainer } : basis;
  })();
  /*
   * What the qualified total cost, folded over the SAME observations.
   *
   * Sherloc reaches its totals from per-channel rows, so the temptation here
   * is `spend / qualifiedVideoViews` off the resolved totals — and that is
   * precisely the arithmetic this metric exists to prevent. `spend` below is
   * the whole campaign's, display included; the qualified total is CTV's and
   * YouTube's alone. The engine folds both halves over one contribution set,
   * so the numerator can never pick up money from a platform the denominator
   * never counted.
   */
  const governedCostPerQualifiedVideoView = governedObservations
    ? aggregateCostPerQualifiedVideoView(governedObservations)
    : null;
  const governedRateValue = (metricKey) =>
    governedBases && governedBases[metricKey] ? governedBases[metricKey].value ?? null : undefined;
  const rateWithGovernance = (explicitValue, metricKey, wholePoolFallback) => {
    if (explicitValue != null) return explicitValue;
    const governedValue = governedRateValue(metricKey);
    if (governedValue !== undefined) return governedValue;
    return wholePoolFallback();
  };

  const impressions = toNumber(safeTotals.impressions ?? unavailableMetric);
  const clicks = toNumber(safeTotals.clicks ?? unavailableMetric);
  /*
   * The engagement COUNT answers to the governed pair, like the video counts.
   *
   * The raw total is a cross-measurement sum: YouTube's ad interactions plus
   * StackAdapt's `landing_page_starts` plus a CTV zero-fill. Printed beside a
   * rate the engine scopes to one declared measurement, no division of the
   * count reaches the rate — 44,761 over anything is not 29.79%%. The pair
   * states the count on the rate's own population and names the rest.
   */
  const engagementsPair =
    governedPairs?.engagements && governedPairs.engagements.count !== null
      ? governedPairs.engagements
      : null;
  const engagements = engagementsPair
    ? engagementsPair.count
    : toNumber(safeTotals.engagements ?? unavailableMetric);
  const videoViews = toNumber(
    safeTotals.video_views !== undefined ? safeTotals.video_views : safeTotals.videoViews ?? unavailableMetric
  );
  const rawSpend = toNumber(safeTotals.spend ?? safeTotals.spend_usd ?? unavailableMetric);
  const spend = resolveClientFacingSpend(
    safeTotals,
    Number.isFinite(rawSpend) ? rawSpend : unavailableMetric
  );
  const conversions = toNumber(safeTotals.conversions ?? unavailableMetric);

  const ctr = rateWithGovernance(
    resolveFieldNumber(safeTotals, ["ctr", "click_through_rate"]),
    "ctr",
    () => computeRatePercent(clicks, impressions)
  );
  /*
   * `engagement_rate` is NOT another spelling of `engagementRate`.
   *
   * It is GA4's SITE figure — engaged sessions over sessions — folded onto the
   * same totals object by the site-analytics pass, while `engagementRate` is
   * the governed PAID rate over the impressions of the platforms that reported
   * engagements. Reading both from one ladder put a session rate on a card
   * labelled Engagement Rate beside a paid engagement count: on Never Stop
   * Chasing, 4,627 of 15,595 sessions rendered as 29.68% underneath
   * "Engagements 41,009". A paid rate falls back to a paid rate or to nothing.
   */
  const er = rateWithGovernance(
    engagementsPair ? engagementsPair.rate : resolveFieldNumber(safeTotals, ["engagementRate"]),
    "engagementRate",
    () => computeRatePercent(engagements, impressions)
  );
  const vvr = rateWithGovernance(
    resolveFieldNumber(safeTotals, ["videoViewRate", "video_view_rate", "viewRate", "view_rate"]),
    "vvr",
    () => computeRatePercent(videoViews, impressions)
  );
  const fallbackCpc =
    clicks > 0 && spend > 0 && Number.isFinite(spend / clicks) ? spend / clicks : null;
  const fallbackCpm =
    impressions > 0 && spend > 0 && Number.isFinite((spend / impressions) * 1000)
      ? (spend / impressions) * 1000
      : null;
  /*
   * CPC and CPM go through the SAME governance gate as the rates above.
   *
   * They were the only two efficiency metrics that skipped it, and the miss was
   * silent because `governedBases` already folded both — the values were
   * computed and then never consulted. On a campaign carrying clickless
   * inventory that omission is the whole point of the gate: Never Stop Chasing
   * has $7,536.45 of CTV spend that cannot produce a click, so whole-pool
   * division printed $15,648.74 / 3,050 = $5.13 on Sherloc Reports while the
   * governed basis gave Campaign Insights $8,112.29 / 3,050 = $2.66. One
   * campaign, one window, two CPCs — the larger one charging television money
   * against clicks that only Display and YouTube could produce.
   *
   * `resolveClientFacingCpc` still runs, as the FALLBACK: it reads an
   * explicitly billed CPC a payload states, which is a pricing fact rather
   * than a division, and its own whole-pool arithmetic is now reached only
   * when there is no per-channel evidence to govern with.
   */
  const cpc = rateWithGovernance(
    resolveFieldNumber(safeTotals, ["cpc", "cost_per_click"]),
    "cpc",
    () => resolveClientFacingCpc(safeTotals, clicks, spend, fallbackCpc)
  );
  const cpm = rateWithGovernance(
    resolveFieldNumber(safeTotals, ["cpm", "cost_per_mille"]),
    "cpm",
    () => resolveClientFacingCpm(safeTotals, impressions, spend, fallbackCpm)
  );

  /*
   * The completion pair and the qualified-video headline, co-scoped upstream.
   * A page renders these; it never rebuilds them.
   */
  const completionsPair =
    governedPairs?.videoCompletions && governedPairs.videoCompletions.count !== null
      ? governedPairs.videoCompletions
      : null;
  const videoCompletions = completionsPair
    ? completionsPair.count
    : resolveFieldNumber(safeTotals, [
        "videoCompletions",
        "video_completions",
        "video_completed",
        "videoCompleted",
        "completedViews",
        "completed_views",
        "completions",
      ]);
  const completionRate = rateWithGovernance(
    completionsPair
      ? completionsPair.rate
      : resolveFieldNumber(safeTotals, [
          "videoCompletionRate",
          "video_completion_rate",
          "completionRate",
          "completion_rate",
        ]),
    "completionRate",
    () => null
  );
  const qualifiedVideoViews =
    governedQualifiedVideo && governedQualifiedVideo.value !== null
      ? governedQualifiedVideo.value
      : resolveFieldNumber(safeTotals, ["qualifiedVideoViews", "qualified_video_views"]);

  const conversionValueRaw =
    safeTotals.conversion_value_usd !== undefined
      ? safeTotals.conversion_value_usd
      : safeTotals.conversionValueUsd !== undefined
      ? safeTotals.conversionValueUsd
      : safeTotals.conversion_value !== undefined
      ? safeTotals.conversion_value
      : safeTotals.conversionValue;

  const conversionValueNum =
    conversionValueRaw == null || conversionValueRaw === "" ? null : toNumber(conversionValueRaw);
  const conversionValue =
    conversionValueNum != null && Number.isFinite(conversionValueNum) ? conversionValueNum : null;

  const ticketSalesRaw =
    safeTotals.ticket_sales_usd ??
    safeTotals.ticketSalesUsd ??
    safeTotals.ticket_sales ??
    safeTotals.ticketSales ??
    safeTotals.ticket_sales_total ??
    safeTotals.ticketSalesTotal;

  const ticketSalesNum =
    ticketSalesRaw == null || ticketSalesRaw === "" ? null : toNumber(ticketSalesRaw);
  const ticketSales =
    ticketSalesNum != null && Number.isFinite(ticketSalesNum) ? ticketSalesNum : null;

  return {
    impressions: Number.isFinite(impressions) ? impressions : unavailableMetric,
    clicks: Number.isFinite(clicks) ? clicks : unavailableMetric,
    engagements: Number.isFinite(engagements) ? engagements : unavailableMetric,
    videoViews: Number.isFinite(videoViews) ? videoViews : unavailableMetric,
    /*
     * The campaign-level video headline.
     *
     * The governed fold over per-channel evidence leads, and the payload field
     * is the fallback. Carrying only the payload field meant the metric existed
     * exactly where the backend had already computed it: the branches that
     * resolve a campaign from per-channel rows — which is how Sherloc reaches
     * its totals whenever the canonical aggregate is thin — had per-platform
     * counts in hand, the registry available, and still no qualified total.
     *
     * NULL when neither resolves, never 0. A zero would claim a CTV+YouTube
     * flight delivered no meaningful video rather than that nothing here
     * carries a governed qualified signal.
     */
    qualifiedVideoViews:
      qualifiedVideoViews != null && Number.isFinite(qualifiedVideoViews)
        ? qualifiedVideoViews
        : null,
    /* The completion pair. Both halves or neither — see `completionsPair`. */
    videoCompletions:
      videoCompletions != null && Number.isFinite(videoCompletions) ? videoCompletions : null,
    completionRate,
    spend: Number.isFinite(spend) ? spend : unavailableMetric,
    clientFacingSpend: Number.isFinite(spend) ? spend : unavailableMetric,
    rawSpend: Number.isFinite(rawSpend) ? rawSpend : unavailableMetric,
    conversions: Number.isFinite(conversions) ? conversions : unavailableMetric,
    ctr,
    er,
    vvr,
    cpc,
    cpm,
    conversionValue,
    ticketSales,
    /* The governed provenance a surface needs in order to say WHY a value is
       absent, and which platforms the present ones describe. */
    rateBasis: governedBases ? { source: "shared_metric_basis", bases: governedBases } : null,
    countRatePairs: governedPairs || null,
    qualifiedVideo: governedQualifiedVideo || null,
    /*
     * The BASIS object, not just its value. The tile states its own scope
     * ("CTV, YouTube spend") and its exact figure in the tooltip, and both
     * come from the numerator/denominator the basis carries. A bare number
     * would make the card unable to explain why it is not Spend ÷ QVV.
     */
    costPerQualifiedVideoView:
      governedCostPerQualifiedVideoView ||
      (() => {
        /* No per-channel evidence: keep the canonical published value if the
           payload carried one, shaped as a basis so the tile reads it the
           same way. Never derived from `spend` above. */
        const published = resolveFieldNumber(safeTotals, [
          "costPerQualifiedVideoView",
          "cost_per_qualified_video_view",
        ]);
        return published != null && Number.isFinite(published)
          ? { value: published, numerator: null, denominator: null, unavailableReason: null }
          : null;
      })(),
  };
};

const resolveReportingTruth = ({
  liveByChannel = {},
  ingestByChannel = {},
  previousResolvedByChannel = {},
} = {}) => {
  const allKeys = new Set([
    ...Object.keys(ingestByChannel || {}),
    ...Object.keys(liveByChannel || {}),
    ...Object.keys(previousResolvedByChannel || {}),
  ]);

  const resolvedByChannel = {};
  for (const rawKey of allKeys) {
    const key = canonicalizeChannelKey(rawKey);
    if (!key) continue;
    const picked = chooseBestChannelView(key, {
      live: liveByChannel[key] || liveByChannel[rawKey],
      previous: previousResolvedByChannel[key] || previousResolvedByChannel[rawKey],
      ingest: ingestByChannel[key] || ingestByChannel[rawKey],
    });
    if (picked) resolvedByChannel[key] = picked;
  }

  const resolvedTotals = computeResolvedTotals(resolvedByChannel);
  return {
    resolvedByChannel,
    resolvedTotals,
    resolvedMetrics: computeReportingMetrics(resolvedTotals, resolvedByChannel),
  };
};

export {
  buildIngestByChannel,
  computeReportingMetrics,
  hasUsableMetrics,
  resolveDisplaySpend,
  resolveChannelSpendBasis,
  resolveClientFacingChannelMetrics,
  resolveReportingTruth,
};
